import React, { createContext, useContext, useMemo } from 'react';
import clsx from 'clsx';

export interface DataTableRow {
  id: string | number;
  label: string | React.ReactNode;
  value: string;
}

export interface DataTableProps {
  /**
   * @deprecated Użyj prop `items` zamiast `data`. Prop `data` zostanie usunięty w przyszłości.
   */
  data?: DataTableRow[];
  /**
   * Tablica danych do automatycznego renderowania wierszy (dla kompatybilności wstecznej)
   */
  items?: DataTableRow[];
  /**
   * Dzieci komponentu - DataTableRow komponenty
   */
  children?: React.ReactNode;
  className?: string;
}

interface DataTableRowContextValue {
  isEven: boolean;
  isFirst: boolean;
  isLast: boolean;
  index: number;
  totalRows: number;
}

const DataTableRowContext = createContext<DataTableRowContextValue | null>(
  null,
);

const useDataTableRowContext = () => {
  const context = useContext(DataTableRowContext);
  if (!context) {
    throw new Error('DataTableRow must be used within DataTable component');
  }
  return context;
};

interface DataTableColumnContextValue {
  isLastColumn: boolean;
  columnIndex: number;
  totalColumns: number;
}

const DataTableColumnContext =
  createContext<DataTableColumnContextValue | null>(null);

const useDataTableColumnContext = () => {
  const context = useContext(DataTableColumnContext);
  // Context jest opcjonalny - jeśli nie ma, zakładamy że to ostatnia kolumna (dla kompatybilności)
  return context || { isLastColumn: true, columnIndex: 0, totalColumns: 1 };
};

// Funkcja pomocnicza do sprawdzania czy element to DataTableColumn
const isDataTableColumnType = (
  type: string | React.JSXElementConstructor<unknown>,
): boolean => {
  if (typeof type === 'string') {
    return false;
  }
  if (type === DataTableColumn) {
    return true;
  }
  if (typeof type === 'object' && type !== null) {
    const componentType = type as { displayName?: string };
    return componentType.displayName === 'DataTableColumn';
  }
  return false;
};

export interface DataTableColumnProps {
  /**
   * Zawartość kolumny
   */
  children: React.ReactNode;
  /**
   * Czy kolumna jest etykietą (lewa kolumna)
   */
  isLabel?: boolean;
  /**
   * Dodatkowe klasy CSS
   */
  className?: string;
  /**
   * Szerokość kolumny (może być Tailwind class np. 'w-1/3', 'w-64' lub wartość CSS np. '200px')
   * Jeśli nie podano, kolumna używa flex-1 (równa szerokość)
   */
  width?: string;
  /**
   * Czy kolumna powinna używać flex-grow (domyślnie true, jeśli nie podano width)
   */
  flex?: boolean;
}

export const DataTableColumn: React.FC<DataTableColumnProps> = ({
  children,
  isLabel = false,
  className,
  width,
  flex,
}) => {
  const columnContext = useDataTableColumnContext();
  const { isLastColumn } = columnContext;

  // Sprawdź czy width to klasa Tailwind (zaczyna się od 'w-') czy wartość CSS
  const isTailwindWidth = width?.startsWith('w-');
  const widthClass = isTailwindWidth ? width : '';
  const widthStyle = width && !isTailwindWidth ? { width } : undefined;

  // Jeśli podano width, nie używaj flex-1 (chyba że flex=true)
  // Jeśli nie podano width, użyj flex-1 (chyba że flex=false)
  const flexClass =
    width && flex !== true ? '' : !width && flex !== false ? 'flex-1' : '';

  return (
    <div
      className={clsx(
        'flex items-center justify-start px-5 py-4 lg:py-6',
        flexClass,
        widthClass,
        {
          'border-r border-grayBorder pl-5 lg:pl-7': isLabel && !isLastColumn,
          'pl-5 lg:pl-7': isLabel && isLastColumn,
          'border-r border-grayBorder': !isLabel && !isLastColumn,
          'pr-7': !isLabel,
        },
        className,
      )}
      style={widthStyle}
    >
      {isLabel ? (
        <div className="font-archivo text-sm font-semibold leading-[1.375] text-text-primary lg:text-base">
          {children}
        </div>
      ) : (
        <span className="font-inter text-sm font-normal leading-[1.571] text-grayText">
          {children}
        </span>
      )}
    </div>
  );
};

// Ustaw displayName dla łatwego porównywania
DataTableColumn.displayName = 'DataTableColumn';

export interface DataTableRowProps {
  /**
   * Identyfikator wiersza (używany jako key)
   */
  id?: string | number;
  /**
   * Dzieci komponentu - DataTableColumn komponenty
   */
  children: React.ReactNode;
  /**
   * Dodatkowe klasy CSS
   */
  className?: string;
}

export const DataTableRow: React.FC<DataTableRowProps> = ({
  id,
  children,
  className,
}) => {
  const context = useDataTableRowContext();
  const { isEven, isFirst, isLast } = context;

  // Przeanalizuj children, aby znaleźć wszystkie kolumny i przekazać informacje o pozycji
  const columnsArray = React.Children.toArray(children);
  const totalColumns = columnsArray.filter((child) => {
    if (React.isValidElement(child)) {
      return isDataTableColumnType(child.type);
    }
    return false;
  }).length;

  return (
    <div
      key={id}
      className={clsx(
        'flex overflow-hidden border-b border-grayBorder',
        className,
        {
          'bg-white': isEven,
          'bg-[#FAFCFF]': !isEven,
          'border-b-0': isLast,
        },
      )}
      style={{
        borderRadius:
          isFirst && isLast
            ? '15px'
            : isFirst
              ? '15px 15px 0 0'
              : isLast
                ? '0 0 15px 15px'
                : '0',
      }}
    >
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          const isDataTableColumn = isDataTableColumnType(child.type);

          if (isDataTableColumn) {
            const columnIndex = columnsArray.slice(0, index + 1).filter((c) => {
              if (React.isValidElement(c)) {
                return isDataTableColumnType(c.type);
              }
              return false;
            }).length;
            const isLastColumn = columnIndex === totalColumns;

            return (
              <DataTableColumnContext.Provider
                key={child.key || index}
                value={{
                  isLastColumn,
                  columnIndex: columnIndex - 1,
                  totalColumns,
                }}
              >
                {child}
              </DataTableColumnContext.Provider>
            );
          }
        }
        return child;
      })}
    </div>
  );
};

// Ustaw displayName dla łatwego porównywania
DataTableRow.displayName = 'DataTableRow';

// Typ dla komponentu DataTable z sub-komponentami
export interface DataTableComponent extends React.FC<DataTableProps> {
  Row: typeof DataTableRow;
  Column: typeof DataTableColumn;
}

const DataTableComponent: React.FC<DataTableProps> = ({
  data,
  items,
  children,
  className,
}) => {
  // Używamy items lub data (dla kompatybilności wstecznej)
  const legacyItems = items || data;

  // Renderuj children jeśli są podane
  const hasChildren = React.Children.count(children) > 0;

  // Renderuj items jeśli są podane i nie ma children
  const shouldRenderItems =
    legacyItems && legacyItems.length > 0 && !hasChildren;

  // Jeśli renderujemy items, konwertujemy je na children
  const rowsToRender = useMemo(() => {
    if (shouldRenderItems) {
      return legacyItems.map((row, index) => {
        const isEven = index % 2 === 0;
        const isFirst = index === 0;
        const isLast = index === legacyItems.length - 1;

        return (
          <DataTableRowContext.Provider
            key={row.id}
            value={{
              isEven,
              isFirst,
              isLast,
              index,
              totalRows: legacyItems.length,
            }}
          >
            <DataTableRow id={row.id}>
              <DataTableColumnContext.Provider
                value={{
                  isLastColumn: false,
                  columnIndex: 0,
                  totalColumns: 2,
                }}
              >
                <DataTableColumn isLabel>{row.label}</DataTableColumn>
              </DataTableColumnContext.Provider>
              <DataTableColumnContext.Provider
                value={{
                  isLastColumn: true,
                  columnIndex: 1,
                  totalColumns: 2,
                }}
              >
                <DataTableColumn>{row.value}</DataTableColumn>
              </DataTableColumnContext.Provider>
            </DataTableRow>
          </DataTableRowContext.Provider>
        );
      });
    }

    // Renderuj children z kontekstem
    const childrenArray = React.Children.toArray(children);
    const totalRows = childrenArray.length;

    return childrenArray.map((child, index) => {
      if (React.isValidElement(child)) {
        const childType = child.type;
        let isDataTableRow = childType === DataTableRow;

        if (
          !isDataTableRow &&
          typeof childType === 'object' &&
          childType !== null
        ) {
          const componentType = childType as { displayName?: string };
          isDataTableRow = componentType.displayName === 'DataTableRow';
        }

        if (isDataTableRow) {
          const isEven = index % 2 === 0;
          const isFirst = index === 0;
          const isLast = index === totalRows - 1;

          return (
            <DataTableRowContext.Provider
              key={child.props.id || index}
              value={{
                isEven,
                isFirst,
                isLast,
                index,
                totalRows,
              }}
            >
              {child}
            </DataTableRowContext.Provider>
          );
        }
      }
      return child;
    });
  }, [shouldRenderItems, legacyItems, children]);

  if (!hasChildren && !shouldRenderItems) {
    return null;
  }

  return (
    <div
      className={clsx(
        'w-full border rounded-card border-grayBorder',
        className,
      )}
    >
      {rowsToRender}
    </div>
  );
};

// Przypisz sub-komponenty do głównego komponentu
export const DataTable = DataTableComponent as DataTableComponent;
DataTable.Row = DataTableRow;
DataTable.Column = DataTableColumn;

// Ustaw displayName dla głównego komponentu
DataTable.displayName = 'DataTable';
